/**
 * TeamUsageView — one Team's drill on the Usage report
 * (/usage/teams/:deskId?from&to; `none` is the "No Team" bucket).
 *
 * The Team's Agent spend over the range beside the same tokens at Claude list
 * (matched tier), then one card per Agent that worked for it, each opening
 * that Agent's drill. Read from the same Team x Agent pivot the report shows,
 * so the two always agree. ADR-0028: sell side only.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { YagniCodeTeamAgentUsageReport } from "@yagni/shared";

import { ListHead } from "../queue/index.js";
import { Note, Sec } from "./usageParts.js";
import {
  NO_TEAM_SEGMENT,
  UsageAccessError,
  agentDrillPath,
  agentLabel,
  basisSavingsPercent,
  fetchTeamAgentUsage,
  formatUsd,
  rangeFromSearch,
  rangeLabel,
  teamLabel,
  teamLede,
} from "./usageReport.js";
import type { FetchTeamAgentUsage } from "./TeamsAndAgentsSection.js";
import {
  ClaudeMethodNote,
  GlassStat,
  GlassTiles,
  SAVE_COLOR,
  savedWithShare,
} from "./usageGlass.js";
import { UsageBreadcrumb, UsageDrillGate } from "./usageDrill.js";

export function TeamUsageRoute() {
  return (
    <UsageDrillGate>
      <TeamUsageView />
    </UsageDrillGate>
  );
}

export function TeamUsageView({
  fetchTeamUsage = fetchTeamAgentUsage,
}: {
  fetchTeamUsage?: FetchTeamAgentUsage;
}) {
  const { deskId: segment = "" } = useParams<{ deskId: string }>();
  const deskId = segment === NO_TEAM_SEGMENT ? null : segment;
  const [search] = useSearchParams();
  const fromParam = search.get("from");
  const toParam = search.get("to");
  const range = useMemo(
    () => rangeFromSearch(new URLSearchParams({ from: fromParam ?? "", to: toParam ?? "" }), new Date()),
    [fromParam, toParam],
  );
  const [report, setReport] = useState<YagniCodeTeamAgentUsageReport | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "forbidden">("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setError(null);
    fetchTeamUsage(range)
      .then((loaded) => {
        if (cancelled) return;
        setReport(loaded);
        setState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof UsageAccessError) setState("forbidden");
        else {
          setError(err instanceof Error ? err.message : "Failed to load Team usage");
          setState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchTeamUsage, range]);

  if (state === "loading") return <Note>Reading the Team's spend</Note>;
  if (state === "forbidden") {
    return (
      <div>
        <UsageBreadcrumb trail={["Teams"]} />
        <Note tone="honest">Team usage is visible to workspace admins.</Note>
      </div>
    );
  }
  if (state === "error" || !report) {
    return (
      <div>
        <UsageBreadcrumb trail={["Teams"]} />
        <Note tone="honest">{error ?? "Failed to load Team usage."}</Note>
      </div>
    );
  }

  const team = report.teams.find((entry) => entry.deskId === deskId) ?? null;
  const name = team ? teamLabel(team) : deskId === null ? "No Team" : "This Team";
  const cells = report.cells.filter((cell) => cell.deskId === deskId);
  // The Team row is gone: there is nothing left to open.
  const deleted = team !== null && team.deskId !== null && team.deskName === null;
  const archived = team?.archived === true;

  return (
    <div data-testid="team-usage-drill" style={{ display: "contents" }}>
      <UsageBreadcrumb trail={["Teams", name]} />
      <ListHead
        title={name}
        sub={["Team", teamLede(rangeLabel(range), deskId, team)]}
        acts={
          deskId !== null && !deleted ? (
            <Link to={`/desks/${encodeURIComponent(deskId)}`} className="btn secondary sm">
              Open the Team
            </Link>
          ) : undefined
        }
      />

      {!team || cells.length === 0 ? (
        <div className="empty dashed tight">
          <p>No Agent spend on this Team in this range.</p>
        </div>
      ) : (
        <>
          <GlassTiles testId="team-tiles">
            <GlassStat
              label="Billed"
              value={formatUsd(team.sellMillicents)}
              sub={`${team.runs.toLocaleString("en-US")} runs · ${team.dispatches.toLocaleString("en-US")} dispatches`}
            />
            <GlassStat
              label="At Claude list"
              value={formatUsd(team.frontier.matched.totalMillicents)}
              sub="same tokens, matched tier"
            />
            <GlassStat
              label="Saved"
              value={savedWithShare(
                team.frontier.matched.savingsMillicents,
                basisSavingsPercent(team.frontier.matched),
              )}
              sub="vs Claude list"
              tone="save"
            />
            <GlassStat
              label="Claude prompt writing"
              value={formatUsd(team.frontier.matched.promptWritingMillicents)}
              sub="included in Claude list"
            />
          </GlassTiles>

          <Sec title="Agents" fact="open one for its runs">
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))" }}
          >
            {cells.map((cell) => {
              const percent = basisSavingsPercent(cell.frontier.matched);
              return (
                <Link
                  key={cell.agentId}
                  to={agentDrillPath(cell.agentId, range)}
                  className="tile no-underline" style={{ display: "block" }}
                  data-testid={`team-agent-${cell.agentId}`}
                >
                  <div className="text-sm font-semibold text-[var(--text-primary)]">
                    {agentLabel(cell)}
                  </div>
                  <div className="mt-2 font-[family-name:var(--font-mono)] text-xl font-semibold text-[var(--text-primary)]">
                    {formatUsd(cell.sellMillicents)}
                  </div>
                  <div className="mt-1 font-[family-name:var(--font-mono)] text-xs text-[var(--text-secondary)]">
                    {formatUsd(cell.frontier.matched.totalMillicents)} at Claude list
                  </div>
                  <div
                    className="mt-0.5 font-[family-name:var(--font-mono)] text-xs"
                    style={{ color: SAVE_COLOR }}
                  >
                    {savedWithShare(cell.frontier.matched.savingsMillicents, percent)} saved
                  </div>
                  <div className="mt-2 text-xs text-[var(--text-tertiary)]">
                    {cell.runs.toLocaleString("en-US")} runs ·{" "}
                    {cell.dispatches.toLocaleString("en-US")} dispatches
                  </div>
                </Link>
              );
            })}
          </div>
          </Sec>
          {team.frontier.counterfactualIncomplete && (
            <p className="fact">
              Some of this Team's Agent spend ran on a lane with no Claude list equivalent, so the
              Claude figures cover part of it rather than guess.
            </p>
          )}
        </>
      )}

      <ClaudeMethodNote />
    </div>
  );
}
